import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { FileText, Save, RotateCcw, Lock, ExternalLink, Type } from "lucide-react";
import { dispatchContentBlockChange } from "../hooks/useContentBlocks";
import PageBlocksManager from "../components/PageBlocksManager";

/**
 * ContentManagementPage — founder/master-admin CMS for every editable text
 * block on the public-facing pages.
 *
 * Tabs split by `page` (prelaunch / beta_launch / launch / global). Each row
 * is a card with the block's label + description, an inline textarea, and
 * Save / Reset-to-default buttons. Member-promoted admins see a read-only
 * banner — server enforces the same with 403 on PUT.
 */
const PAGE_TABS = [
  { key: "prelaunch", label: "Pre-launch page" },
  { key: "beta_launch", label: "Beta launch page" },
  { key: "launch", label: "Public launch" },
  { key: "global", label: "Global" },
];

const ContentManagementPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState("prelaunch");
  const [view, setView] = useState("text-blocks"); // "text-blocks" | "custom-blocks"
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const canEdit = useMemo(() => {
    const email = (user?.email || "").toLowerCase();
    return (
      email === "ramneetrdh@gmail.com" ||
      email === "admin@dhdh.ca" ||
      user?.admin_role === "founder"
    );
  }, [user]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/content-blocks`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load content blocks");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return items.filter(it => {
      if (it.page !== page) return false;
      if (!term) return true;
      return (
        (it.block_key || "").toLowerCase().includes(term) ||
        (it.label || "").toLowerCase().includes(term) ||
        (it.description || "").toLowerCase().includes(term) ||
        (it.value || "").toLowerCase().includes(term) ||
        (it.default_value || "").toLowerCase().includes(term)
      );
    });
  }, [items, page, searchTerm]);

  // Group filtered rows by section prefix (the part of block_key before the first dot).
  const grouped = useMemo(() => {
    const groups = {};
    for (const it of filtered) {
      const prefix = (it.block_key || "").split(".")[0] || "general";
      (groups[prefix] = groups[prefix] || []).push(it);
    }
    return groups;
  }, [filtered]);

  // Sorted section list — pre-computed so JSX doesn't sort on every render.
  const groupedSections = useMemo(
    () => Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)),
    [grouped],
  );

  const handleChange = (block_key, draftValue) => {
    setItems(prev => prev.map(it => it.block_key === block_key
      ? { ...it, _draft: draftValue, _dirty: true }
      : it));
  };

  const effectiveValue = (it) => (it._draft !== undefined ? it._draft : (it.value || it.default_value || ""));

  const handleSave = async (block_key) => {
    const row = items.find(it => it.block_key === block_key);
    if (!row) return;
    const v = (row._draft !== undefined ? row._draft : row.value || "").trim();
    if (!v) { toast.error("Value cannot be empty (use Reset to default to revert)"); return; }
    setSavingKey(block_key);
    try {
      const res = await axios.put(`${API}/admin/content-blocks/${encodeURIComponent(block_key)}`, { value: v });
      setItems(prev => prev.map(it => it.block_key === block_key ? { ...res.data, _dirty: false, _draft: undefined } : it));
      dispatchContentBlockChange(block_key, v);
      toast.success("Saved — live on the public page");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSavingKey(null); }
  };

  const handleReset = async (block_key) => {
    const row = items.find(it => it.block_key === block_key);
    if (!row) return;
    if (!window.confirm(`Reset "${row.label}" to its default? This deletes your custom copy for this block.`)) return;
    setSavingKey(block_key);
    try {
      await axios.delete(`${API}/admin/content-blocks/${encodeURIComponent(block_key)}`);
      setItems(prev => prev.map(it => it.block_key === block_key ? { ...it, value: "", _draft: undefined, _dirty: false } : it));
      dispatchContentBlockChange(block_key, row.default_value || "");
      toast.success("Reset to default");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setSavingKey(null); }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="content-management-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <FileText className="inline w-7 h-7 mr-2 -mt-1 text-[#8DB5A0]" />
              Content Management
            </h1>
            <p className="text-[#64748B] mt-1">
              Edit every text block on the public-facing pages. Save → goes live instantly.
              You can also click the pencil ✏️ icon next to any text on the actual page.
            </p>
          </div>
          {!canEdit && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-xs font-medium">
              <Lock className="w-3.5 h-3.5" />
              Read-only — founder or master admin only
            </span>
          )}
        </div>

        {/* Founder live-page preview links. Both URLs are the real production
            dhdh.ca routes. The beta-launch URL is reachable for any admin and
            any beta-cookie holder. The `?preview=launch` query is a founder-
            only override that renders the post-launch LandingPage on the root
            URL even while LAUNCH_MODE=prelaunch — see RootLanding in App.js. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-testid="cms-live-preview-links">
          <a
            href="https://dhdh.ca/beta-launch"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-start gap-3 p-4 rounded-2xl border border-[#E5DFEC] bg-gradient-to-br from-white to-[#F0ECF7]/40 hover:border-[#6B4E8A]/40 hover:shadow-sm transition-all"
            data-testid="cms-preview-beta-launch"
          >
            <ExternalLink className="w-5 h-5 text-[#6B4E8A] mt-0.5 shrink-0 group-hover:translate-x-0.5 transition-transform" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Preview Beta Launch Page</p>
              <p className="text-xs text-[#64748B] mt-0.5">Opens <span className="font-mono">https://dhdh.ca/beta-launch</span> — what beta testers land on after redeeming.</p>
            </div>
          </a>
          <a
            href="https://dhdh.ca/?preview=launch"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-start gap-3 p-4 rounded-2xl border border-[#E5DFEC] bg-gradient-to-br from-white to-[#F0ECF7]/40 hover:border-[#6B4E8A]/40 hover:shadow-sm transition-all"
            data-testid="cms-preview-public-launch"
          >
            <ExternalLink className="w-5 h-5 text-[#6B4E8A] mt-0.5 shrink-0 group-hover:translate-x-0.5 transition-transform" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Preview Public Launch Page</p>
              <p className="text-xs text-[#64748B] mt-0.5">Opens <span className="font-mono">https://dhdh.ca/?preview=launch</span> — the post-launch landing, even while in prelaunch.</p>
            </div>
          </a>
        </div>

        {/* Top-level view switcher: existing text-blocks vs new custom blocks */}
        <div className="inline-flex flex-wrap gap-1 rounded-full bg-white border border-[#E5DFEC] p-1 mb-4" data-testid="cms-view-tabs">
          <button
            onClick={() => setView("text-blocks")}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${view === "text-blocks" ? "bg-[#6B4E8A] text-white" : "text-[#64748B] hover:text-[#6B4E8A]"}`}
            data-testid="cms-view-text-blocks"
          >
            <FileText className="inline w-3.5 h-3.5 mr-1 -mt-0.5" /> Built-in text
          </button>
          <button
            onClick={() => setView("custom-blocks")}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${view === "custom-blocks" ? "bg-[#6B4E8A] text-white" : "text-[#64748B] hover:text-[#6B4E8A]"}`}
            data-testid="cms-view-custom-blocks"
          >
            <Type className="inline w-3.5 h-3.5 mr-1 -mt-0.5" /> Custom text blocks
          </button>
        </div>

        {view === "custom-blocks" ? (
          <PageBlocksManager />
        ) : (
        <>
        {/* Page tabs */}
        <div className="inline-flex flex-wrap gap-1 rounded-full bg-white border border-[#E5DFEC] p-1 mb-4" data-testid="cms-page-tabs">
          {PAGE_TABS.map(t => {
            const count = items.filter(it => it.page === t.key).length;
            return (
              <button
                key={t.key}
                onClick={() => setPage(t.key)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
                  page === t.key ? "bg-[#6B4E8A] text-white" : "text-[#64748B] hover:text-[#6B4E8A]"
                }`}
                data-testid={`cms-tab-${t.key}`}
              >
                {t.label} <span className="opacity-60">({count})</span>
              </button>
            );
          })}
        </div>

        {/* Search bar */}
        <div className="mb-6 max-w-xl">
          <Input
            type="search"
            placeholder="Search by key, label, description, or current value…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-white border-[#E5DFEC]"
            data-testid="cms-search-input"
          />
          {searchTerm && (
            <p className="text-xs text-[#64748B] mt-1.5">
              Showing {filtered.length} match{filtered.length === 1 ? "" : "es"} on the <span className="font-medium text-[#6B4E8A]">{PAGE_TABS.find(t => t.key === page)?.label}</span> tab.
            </p>
          )}
        </div>

        {loading ? (
          <div className="space-y-3">{[1,2,3,4].map(i => <div key={i} className="h-32 bg-[#F0ECF7] rounded-2xl animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-[#E5DFEC] rounded-2xl p-10 text-center">
            <FileText className="w-10 h-10 mx-auto mb-3 text-[#E5DFEC]" />
            <p className="text-[#6B4E8A] font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>No editable blocks on this page yet</p>
            <p className="text-sm text-[#64748B] mt-1">When new blocks are added to the codebase they appear here automatically.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {groupedSections.map(([sectionKey, rows]) => (
              <div key={sectionKey} data-testid={`cms-section-${sectionKey}`}>
                <h3
                  className="text-xs uppercase tracking-widest text-[#8DB5A0] font-semibold mb-2"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {sectionKey.replace(/_/g, ' ')} <span className="text-[#94A3B8]">· {rows.length}</span>
                </h3>
                <div className="space-y-3">
                  {rows.map(it => {
              const value = effectiveValue(it);
              const isOverride = (it.value || "").length > 0 && it.value !== it.default_value;
              const isLong = (it.default_value || "").length > 120 || (value || "").length > 120;
              return (
                <div key={it.block_key} className="bg-white border border-[#E5DFEC] rounded-2xl p-5" data-testid={`cms-row-${it.block_key}`}>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{it.label || it.block_key}</p>
                      {it.description && <p className="text-xs text-[#64748B] mt-0.5">{it.description}</p>}
                      <code className="inline-block text-[10px] text-[#94A3B8] mt-1 px-1.5 py-0.5 bg-[#FAF7F2] rounded">{it.block_key}</code>
                    </div>
                    {isOverride && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-[#10B981] bg-[#ECFDF5] px-2 py-1 rounded-full">
                        Custom override active
                      </span>
                    )}
                  </div>

                  {isLong ? (
                    <Textarea
                      value={value}
                      onChange={(e) => handleChange(it.block_key, e.target.value)}
                      disabled={!canEdit}
                      maxLength={5000}
                      rows={Math.max(3, Math.min(8, value.split("\n").length + 1))}
                      className="bg-white border-[#E5DFEC] mb-2"
                      data-testid={`cms-input-${it.block_key}`}
                    />
                  ) : (
                    <Input
                      value={value}
                      onChange={(e) => handleChange(it.block_key, e.target.value)}
                      disabled={!canEdit}
                      maxLength={500}
                      className="bg-white border-[#E5DFEC] mb-2"
                      data-testid={`cms-input-${it.block_key}`}
                    />
                  )}

                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-[#94A3B8]">
                      {it._dirty
                        ? <span className="text-[#D97706] font-medium">Unsaved changes</span>
                        : isOverride ? "Custom override saved" : "Using default"}
                      {" · "}default: <span className="italic">{(it.default_value || "").slice(0, 60)}{it.default_value?.length > 60 ? "…" : ""}</span>
                    </span>
                    <div className="flex items-center gap-2">
                      {isOverride && canEdit && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReset(it.block_key)}
                          disabled={savingKey === it.block_key}
                          className="rounded-full text-xs"
                          data-testid={`cms-reset-${it.block_key}`}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" /> Reset
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => handleSave(it.block_key)}
                        disabled={!canEdit || !it._dirty || savingKey === it.block_key}
                        className="rounded-full bg-[#6B4E8A] text-white hover:bg-[#553D6F]"
                        data-testid={`cms-save-${it.block_key}`}
                      >
                        {savingKey === it.block_key ? "Saving…" : <><Save className="w-3.5 h-3.5 mr-1" /> Save</>}
                      </Button>
                    </div>
                  </div>
                </div>
              );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        )}
      </div>
    </Layout>
  );
};

export default ContentManagementPage;
